import type React from 'react';
import { type ReactNode, useState } from 'react';
import type { APIError } from '../../../api/types';
import styles from './ErrorDisplay.module.css';

export interface ErrorDisplayProps {
  error: APIError | Error | string;
  onDismiss?: () => void;
  variant?: 'banner' | 'inline' | 'boundary';
  showDetails?: boolean;
  children?: ReactNode;
}

export const ErrorDisplay: React.FC<ErrorDisplayProps> = ({
  error,
  onDismiss,
  variant = 'banner',
  showDetails = false,
  children,
}) => {
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  const errorMessage =
    typeof error === 'string' ? error : error.message || 'An unexpected error occurred';

  const errorStatus = typeof error === 'object' && 'status' in error ? error.status : undefined;

  const getHintForStatus = (status?: number): string | null => {
    switch (status) {
      case 401:
        return 'Check your authentication token';
      case 403:
        return 'Access denied. Check your permissions';
      case 404:
        return 'The requested resource was not found';
      case 429:
        return 'Too many requests. Please try again later';
      case 500:
        return 'Server error. Please try again later';
      default:
        return null;
    }
  };

  const hint = errorStatus ? getHintForStatus(errorStatus) : null;

  if (variant === 'boundary') {
    return (
      <div className={styles.boundaryContainer}>
        <div className={styles.boundaryContent}>
          <span className={styles.icon}>⚠️</span>
          <h3 className={styles.boundaryTitle}>Something went wrong</h3>
          <p className={styles.boundaryMessage}>{errorMessage}</p>
          {showDetails && error instanceof Error && error.stack && (
            <details className={styles.details}>
              <summary className={styles.detailsSummary}>Error details</summary>
              <pre className={styles.detailsCode}>{error.stack}</pre>
            </details>
          )}
          {children}
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.container} ${styles[variant]}`} role="alert">
      <div className={styles.content}>
        <span className={styles.icon} aria-hidden="true">
          ⚠️
        </span>
        <div className={styles.textWrapper}>
          <div className={styles.text}>
            <strong>Error:</strong> {errorMessage}
          </div>
          {hint && <div className={styles.hint}>{hint}</div>}
          {showDetails && error instanceof Error && error.stack && (
            <details
              className={styles.details}
              open={isDetailsOpen}
              onToggle={(e) => setIsDetailsOpen(e.currentTarget.open)}
            >
              <summary className={styles.detailsSummary}>Show details</summary>
              <pre className={styles.detailsCode}>{error.stack}</pre>
            </details>
          )}
        </div>
        {onDismiss && (
          <button
            className={styles.dismissButton}
            onClick={onDismiss}
            aria-label="Dismiss error"
            type="button"
          >
            ×
          </button>
        )}
      </div>
    </div>
  );
};
